import React from 'react';
import styled from 'styled-components';
import { Mail } from 'lucide-react';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

function escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, ch => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[ch]));
}

/* Destaca `{{variavel}}` visualmente na prévia — cru, o produtor não tem como
   saber de cara o que vira personalização e o que é texto literal. Seguro
   aplicar em cima do HTML do corpo (já sanitizado/gerado pelo próprio editor,
   nunca de fora) porque o token nunca aparece dentro de atributo de tag. */
function highlightVariables(html) {
    return String(html || '').replace(/\{\{\s*(\w+)\s*\}\}/g, '<span class="ecx-var">{{$1}}</span>');
}

export default function EmailPreviewFrame({
    fromName = 'Whizr',
    fromEmail = 'noreply@whizr.app',
    subject = '',
    bodyHtml = '',
    emptyHint = 'Sua mensagem aparece aqui conforme você escreve.',
}) {
    const hasContent = subject.trim().length > 0 || bodyHtml.trim().length > 0;

    return (
        <Frame>
            <FrameBar>
                <FrameDot $color='#ED6A5E' />
                <FrameDot $color='#F4BF4F' />
                <FrameDot $color='#61C554' />
            </FrameBar>

            <FrameHeader>
                <Avatar><Mail size={14} strokeWidth={2.2} /></Avatar>
                <HeaderText>
                    <FromLine>
                        <FromName>{fromName}</FromName>
                        <FromEmail>&lt;{fromEmail}&gt;</FromEmail>
                    </FromLine>
                    <SubjectLine
                        dangerouslySetInnerHTML={{ __html: subject.trim() ? highlightVariables(escapeHtml(subject)) : '<span class="ecx-placeholder">Sem assunto</span>' }}
                    />
                </HeaderText>
            </FrameHeader>

            {hasContent ? (
                <FrameBody dangerouslySetInnerHTML={{ __html: highlightVariables(bodyHtml) }} />
            ) : (
                <FrameEmpty>{emptyHint}</FrameEmpty>
            )}
        </Frame>
    );
}

const Frame = styled.div`
    border-radius: 12px;
    border: 1px solid ${colors.neutralDark};
    background: #fff;
    overflow: hidden;
`;

const FrameBar = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 14px;
    background: ${colors.neutralBackground};
    border-bottom: 1px solid ${colors.neutralDark};
`;

const FrameDot = styled.span`
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
    opacity: 0.85;
`;

const FrameHeader = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px 16px;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const Avatar = styled.span`
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${panelAccentAlpha(colors.secondaryPurple, '15')};
    color: ${panelAccent(colors.secondaryPurple)};
`;

const HeaderText = styled.div`
    min-width: 0;
    flex: 1;
`;

const FromLine = styled.div`
    display: flex;
    align-items: baseline;
    gap: 6px;
    flex-wrap: wrap;
`;

const FromName = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const FromEmail = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
`;

const SubjectLine = styled.p`
    margin: 4px 0 0;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
    word-break: break-word;

    .ecx-placeholder {
        font-weight: 400;
        color: ${colors.nightLight};
    }

    .ecx-var {
        background: ${panelAccentAlpha(colors.secondaryPurple, '15')};
        color: ${panelAccent(colors.secondaryPurple)};
        border-radius: 4px;
        padding: 0 3px;
        font-weight: 700;
    }
`;

const FrameBody = styled.div`
    max-height: 260px;
    overflow-y: auto;
    padding: 16px;
    font-size: 14px;
    line-height: 1.6;
    color: ${colors.nightDark};

    img {
        max-width: 100%;
        height: auto;
    }

    .ecx-var {
        background: ${panelAccentAlpha(colors.secondaryPurple, '15')};
        color: ${panelAccent(colors.secondaryPurple)};
        border-radius: 4px;
        padding: 0 3px;
        font-weight: 700;
    }
`;

const FrameEmpty = styled.div`
    padding: 32px 16px;
    text-align: center;
    font-size: 13px;
    color: ${colors.nightLight};
`;
